/**
 * "Bekerja di: {tempat}" (plan 10, spec 2026-10-02 admin-keuskupan-di-paroki 4): an admin
 * keuskupan works in the diocese (Keuskupan, the default) or inside one parish with that
 * parish's Admin Paroki rights. The list is Keuskupan and every parish of the device's units by
 * name (`admin:tempat_kerja`). A choice that would leave unsaved work asks "Tinggalkan tanpa
 * menyimpan?" first (the one leave guard); then the worker, this device's copy and the screens
 * move together (`gantiTempatKerja`) and the page is Beranda, so no page of one parish stays
 * open under another. AppShell shows it to an admin keuskupan only: at the top of the side panel
 * (`ringkas`: the rail's icon) and of the phone's Lainnya.
 *
 * While the worker is being told, a second choice does nothing (a ref, taken before the busy
 * state renders; the trigger is `aria-disabled` with the `SIBUK` look). The list is read again
 * after every save or pulled page: a new name or Nama pendek of the parish worked in (Pengaturan
 * paroki) reaches the header and this person's stored choice (`segarkanTempatKerja`); a parish
 * the list no longer names (deleted) takes the person back to Keuskupan through the switch
 * (`lepasTempatHilang`) and to Beranda.
 *
 * A choice that did not go through, or a parish that is gone, is said in one sentence: under the
 * trigger on the open panel and in Lainnya, and read out by the shell's polite region
 * (`useUmumkan`, PindahHalaman), which stays mounted when Lainnya closes and on the rail, where
 * the trigger is the icon with its tooltip.
 */
import { format, uiStrings } from '@satu/shared'
import { ChevronDown, MapPin } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { type NavigateFunction, useNavigate } from 'react-router'
import { getDb } from '@/app/db'
import { useJaga } from '@/app/store/jaga'
import {
  type GantiTempatDeps,
  gantiTempatKerja,
  lepasTempatHilang,
  segarkanTempatKerja,
  type TempatKerja,
  useSesi,
} from '@/app/store/sesi'
import { browserStorage } from '@/app/store/tampilan'
import { useUi } from '@/app/store/ui'
import { useUmumkan } from '@/app/store/umumkan'
import { SIBUK } from '@/components/primitives/Wizard'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useWorkerRead } from '@/hooks/use-worker-read'
import { cn } from '@/lib/utils'

const teks = uiStrings.bekerjaDi
/** The radio value of the diocese view (a parish's is its UUID). */
const KEUSKUPAN = 'keuskupan'

/** The worker's `sesi:kerja` and this device's storage, as every move of the switch uses them. */
const depsGanti = (): GantiTempatDeps => ({
  kirim: (kerja) => getDb().request('sesi:kerja', { kerja }),
  storage: browserStorage(),
})

/** The session's parish moved: Lainnya closes and the page is Beranda (no page of one stays). */
function keBeranda(navigate: NavigateFunction): void {
  useUi.getState().setLainnyaOpen(false)
  void navigate('/')
}

export function PilihTempatKerja({ ringkas = false }: { ringkas?: boolean }) {
  const bekerjaDi = useSesi((s) => s.bekerjaDi)
  const navigate = useNavigate()
  const { data } = useWorkerRead<TempatKerja[]>('admin:tempat_kerja', {})
  // The sentence under the trigger: a choice that did not go through, or a parish that is gone.
  const [kabar, setKabar] = useState<string | null>(null)
  const [sibuk, setSibuk] = useState(false)
  const berjalan = useRef(false)
  const daftar = data ?? []
  const pemicu = format(teks.pemicu, { tempat: bekerjaDi?.nama ?? teks.keuskupan })

  // Shown here, read out by the shell (its region outlives Lainnya closing).
  const kabarkan = useCallback((kalimat: string) => {
    setKabar(kalimat)
    useUmumkan.getState().umumkan(kalimat)
  }, [])

  useEffect(() => {
    if (data == null || bekerjaDi === null) return
    segarkanTempatKerja(data, browserStorage())
    lepasTempatHilang(data, depsGanti()).then(
      (pergi) => {
        if (!pergi) return
        kabarkan(teks.hilang)
        // The parish's screens go with it: nothing there can be saved any more.
        useJaga.getState().lewati()
        keBeranda(navigate)
      },
      () => {},
    )
  }, [data, bekerjaDi, navigate, kabarkan])

  const pilih = (nilai: unknown) => {
    if (berjalan.current) return
    const asal = bekerjaDi?.parokiId ?? null
    const tujuan =
      nilai === KEUSKUPAN ? null : (daftar.find((p) => p.parokiId === nilai) ?? undefined)
    if (tujuan === undefined || (tujuan?.parokiId ?? null) === asal) return
    useJaga.getState().tanya(() => {
      if (berjalan.current) return
      berjalan.current = true
      setSibuk(true)
      setKabar(null)
      gantiTempatKerja(tujuan, depsGanti())
        .then(
          (kini) => {
            // A parish the worker or this device did not take (the answer is null): one sentence.
            // Whenever the session's parish moved, the page is Beranda.
            if (tujuan !== null && kini === null) kabarkan(teks.gagal)
            if ((useSesi.getState().bekerjaDi?.parokiId ?? null) === asal) return
            keBeranda(navigate)
          },
          () => kabarkan(teks.gagal),
        )
        .finally(() => {
          berjalan.current = false
          setSibuk(false)
        })
    })
  }

  const ikon = <MapPin size={20} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />
  const gaya =
    'flex h-9 items-center gap-2 text-table text-text hover-band press focus-settle-inset pointer-coarse:h-11'

  return (
    <div className={ringkas ? 'flex flex-col items-center py-2' : 'px-2 py-2'}>
      <DropdownMenu>
        {ringkas ? (
          // The rail's icon is named as the other rail icons are: its label, and a tooltip.
          <Tooltip>
            <TooltipTrigger
              render={
                <DropdownMenuTrigger
                  aria-label={pemicu}
                  aria-disabled={sibuk || undefined}
                  className={cn(gaya, SIBUK, 'w-10 justify-center pointer-coarse:w-11')}
                />
              }
            >
              {ikon}
            </TooltipTrigger>
            <TooltipContent side="right">{pemicu}</TooltipContent>
          </Tooltip>
        ) : (
          <DropdownMenuTrigger
            aria-disabled={sibuk || undefined}
            className={cn(gaya, SIBUK, 'w-full px-2')}
          >
            {ikon}
            <span className="min-w-0 flex-1 truncate text-left">{pemicu}</span>
            <ChevronDown size={16} aria-hidden="true" className="shrink-0 text-muted" />
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuRadioGroup value={bekerjaDi?.parokiId ?? KEUSKUPAN} onValueChange={pilih}>
            {/* Base UI's group label must sit inside its group (MenuGroupContext). */}
            <DropdownMenuLabel>{teks.label}</DropdownMenuLabel>
            <DropdownMenuRadioItem value={KEUSKUPAN} closeOnClick>
              {teks.keuskupan}
            </DropdownMenuRadioItem>
            {daftar.map((p) => (
              <DropdownMenuRadioItem key={p.parokiId} value={p.parokiId} closeOnClick>
                {p.nama}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {kabar !== null && !ringkas ? (
        // Read out by the shell's region; the rail (56 px) shows it once the panel opens.
        <p className="px-2 pt-1 text-label text-text">{kabar}</p>
      ) : null}
    </div>
  )
}
